import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as FileSaver from 'file-saver';
import { BlobService } from './blob.service';
import { FileService } from './file.service';

@Injectable()
export class FileDownloadService {
    private readonly mimeTypes: { [key: string]: string } = {
        // Document formats
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'odt': 'application/vnd.oasis.opendocument.text',
        'ods': 'application/vnd.oasis.opendocument.spreadsheet',
        'odp': 'application/vnd.oasis.opendocument.presentation',

        // Image formats
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'bmp': 'image/bmp',
        'svg': 'image/svg+xml',
        'webp': 'image/webp',

        // Audio formats
        'mp3': 'audio/mpeg',
        'wav': 'audio/wav',
        'ogg': 'audio/ogg',

        // Video formats
        'mp4': 'video/mp4',
        'avi': 'video/x-msvideo',
        'webm': 'video/webm',

        // Archive formats
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
        '7z': 'application/x-7z-compressed',

        // Text formats
        'txt': 'text/plain',
        'csv': 'text/csv',
        'html': 'text/html',
        'xml': 'application/xml',
        'json': 'application/json',

        // Other common formats
        'rtf': 'application/rtf',
        'snote': 'application/octet-stream',

        // Add more as needed
    };

    private readonly mimeTypeToExtension: { [key: string]: string } =
        Object.entries(this.mimeTypes)
              .reduce((acc, [ext, mime]) => {
                  // Only set if it doesn't already exist to avoid overwriting preferred extensions
                  if (!acc[mime]) {
                      acc[mime] = ext;
                  }
                  return acc;
              }, {});

    public constructor(private sanitizer: DomSanitizer,
                       private blobService: BlobService,
                       private fileService: FileService,
    ) {
    }

    public downloadFile(base64Data: string, fileName: string, providedMimeType?: string): void {
        let mimeType: string;

        const extension = this.fileService.getExtensionFromFileName(fileName);
        if (providedMimeType) {
            mimeType = providedMimeType;
        } else {
            mimeType = extension ? this.fileService.getMimeTypeFromExtension(extension) : this.fileService.detectMimeType(base64Data);
        }

        if (!extension && mimeType) {
            const mappedExtension = this.mimeTypeToExtension[mimeType] || mimeType.split('/')[1];
            fileName += `.${ mappedExtension }`;
        }

        const arrBuffer = this.fileService.base64ToArrayBuffer(base64Data);
        const blob = new Blob([arrBuffer], { type: mimeType });
        this.downloadBlob(blob, fileName);
    }

    public downloadBlob(blob: Blob, fileName: string): void {
        const url = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        document.body.appendChild(downloadLink);
        downloadLink.href = url;
        downloadLink.download = fileName; // Use the original file name
        downloadLink.click();
        window.URL.revokeObjectURL(url);
        downloadLink.remove();
    }

    /**
     * Downloads the Base64 content as PDF File.
     *
     * @param {any} content
     * @param fileName
     */
    public downloadAsPDF(content: string, fileName = 'download.pdf') {
        const blob = this.blobService.base64PdfToBlob(content);
        // const blobUrl = this.blobService.getBlobObjectURL(blob);
        // this.sanitizer.bypassSecurityTrustUrl(blobUrl);
        // window.open(blobUrl);

        FileSaver.saveAs(blob, fileName);
    }

    public openPDF(content: string, fileName = 'download.pdf') {
        const blob = this.blobService.base64PdfToBlob(content);
        const blobUrl = this.blobService.getBlobObjectURL(blob);
        this.sanitizer.bypassSecurityTrustUrl(blobUrl);
        window.open(blobUrl);
    }
}
